import React from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import {
    Drawer,
    IconButton,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { RuleSetType, AdminSidePanelAction, RequestTypes, RequestSubtypes } from '../../../Enums';
import { RESET_RULE_SET, createRuleSet, editRuleSet } from '../../store/ruleSet';
import InformationSection from './Step1-InformationSection';
import BreakRuleSection from './Step2-BreakRuleSection';
import SortingRuleSection from './Step2-SortingRuleSection';
import EmployeeRulesSection from './Step2-EmployeeRulesSection';
import ScheduleRulesSection from './Step3-ScheduleRulesSection';
import ShiftRulesSection from './Step4-ShiftRulesSection';
import { hasAllowOverlapRule, hasValidatePaycodeOverlapRule, hasTimeBeforeOrAfterShiftStartAllowedRule, hasMultipleShiftOnSameDayRule } from './Step4-ShiftRulesSection/Step4-ShiftRulesSection.service';
import ActionButtons from '../../../../common/components/UI/ActionButtons/ActionButtons';
import './RuleSetEdit.scss';

// Mirror ConsecutiveHoursRuleHelper.CONSECUTIVE_MINUTES_MINIMUM / _MAXIMUM
const CONSECUTIVE_MINUTES_MINIMUM = 15;
const CONSECUTIVE_MINUTES_MAXIMUM = 72 * 60;

const DEFAULT_VALIDATION_ERRORS = {
    name: false,
    ruleSetType: false,
    requestType: false,
    requestSubtype: false,
    awardingRules: false,
    jobTransferValidationType: false,
    probationLength: false,
    probationDataviewId: false,
    probationDataviewKeyAlias: false,
    probationCustomDateFormat: false,
    restrictionDataviewId: false,
    restrictionDataviewKeyAlias: false,
    restrictionCustomDateFormat: false,
    restrictionStartAfter: false,
    restrictionDuration: false,
    overlapLength: false,
    paycodeOverlapLength: false,
    employeeEligibilityLength: false,
    employeeEligibilityDataviewId: false,
    employeeEligibilityDataviewKeyAlias: false,
    employeeEligibilityPeriod: false,
    timeBeforeShiftLength: false,
    timeBeforeShiftType: false,
    maxMultipleShiftConsecutiveDay: false,
    maxMultipleShiftPerWeek: false,
    minConsecutiveHoursInMinutes: false,
    maxConsecutiveHoursInMinutes: false,
    consecutiveHoursNoBound: false,
    consecutiveHoursRange: false,
    isMaxHoursPerRollingPeriod: false,
    noPaycodesSelected: false,
    noSameDayPaycodesSelected: false,
    noBreakRuleCondition: false,
    noBreakRuleSameConditionAndShiftDuration: false
}
/**
 * @param {{ open: boolean; setOpen: Function; }} props
 */
function RuleSetEdit(props) {
    const dispatch = useDispatch();
    const { t } = useTranslation('employmentBillboard', { keyPrefix: 'settings.ruleSet' });

    //const [isFormInvalid, setIsFormInvalid] = useState(true); // TODO D. 2023-04-11 - When is duplicate, submit should be enabled. When edit, no. 

    /** @type {RuleSet.IRuleSetDetails} */
    const adminRuleSet = useSelector((/** @type {EmploymentBillboard.AdminStore} */ state) => state.adminRuleSet);
    /** @type {AdminAppData.IAppData} */
    const adminAppData = useSelector((/** @type {EmploymentBillboard.AdminStore} */ state) => state.adminAppData);
    const {
        ruleSetId,
        name,
        ruleSetType,
        requestType,
        requestSubtype,
        awardingRules,
        validationRuleSet,
        breakRules,
        adminSidePanelAction,
        isEditable,
        isActionLoading,
    } = adminRuleSet;

    const { ruleSets } = adminAppData;
    const { open, setOpen } = props;

    const [validationErrors, setValidationErrors] = React.useState(_.clone(DEFAULT_VALIDATION_ERRORS));

    const validate = () => {
        let hasError = false;
        const errors = _.clone(DEFAULT_VALIDATION_ERRORS);

        // Information section
        if (!name.trim()) {
            hasError = true;
            errors.name = true;
        } else {
            const value = ruleSets.find((x) => x.name === name && x.ruleSetId !== ruleSetId);
            if (value) {
                hasError = true;
                errors.name = true;
            }
        }
        if (ruleSetType === -1) {
            hasError = true;
            errors.ruleSetType = true;
        }

        // Sorting rule section
        if (ruleSetType === RuleSetType.AwardingRule && (!awardingRules || !awardingRules.length)) {
            hasError = true;
            errors.awardingRules = true;
        }

        // Employee rules section
        if (ruleSetType === RuleSetType.ValidationRule) {
            const {
                jobTransferValidationType,
                isOnProbation,
                probationLength,
                probationDataviewId,
                probationDataviewKeyAlias,
                probationCustomDateFormat,
                isOnRestriction,
                restrictionDataviewId,
                restrictionDataviewKeyAlias,
                restrictionCustomDateFormat,
                restrictionStartAfter,
                restrictionDuration,
                isAllowShiftOverlap,
                overlapLength,
                isEmployeeEligibility,
                employeeEligibilityLength,
                employeeEligibilityDataviewId,
                employeeEligibilityDataviewKeyAlias,
                employeeEligibilityPeriod,
                isTimeBeforeOrAfterShiftStartAllowed,
                timeBeforeShiftLength,
                timeBeforeShiftType,
                isMultipleShiftOnSameDayRuleEnabled,
                maxMultipleShiftConsecutiveDay,
                maxMultipleShiftPerWeek,
                isMaxHoursPerRollingPeriod,
                maxHoursInRollingPeriodRules,
                isValidatePaycodeOverlap,
                paycodeOverlapLength,
                overlapPaycodeListToValidate,
                isValidateSameDayPaycode,
                sameDayPaycodeListToValidate,
                isOverrideMaxHoursPerWeek,
                isOverrideMaxHoursPerDay,
                overrideMaxHoursPerWeekInMinutes,
                overrideMaxHoursPerDayInMinutes,
                isMaxHoursPerDay,
                isMaxHoursPerWeek,
                isConsecutiveHours,
                isMinConsecutiveHours,
                minConsecutiveHoursInMinutes,
                isMaxConsecutiveHours,
                maxConsecutiveHoursInMinutes,
            } = validationRuleSet;

            if (requestType === null) {
                hasError = true;
                errors.requestType = true;
            }
            if (requestType === RequestTypes.openShift && requestSubtype === null) {
                hasError = true;
                errors.requestSubtype = true;
            }
            const isVto = requestType === RequestTypes.voluntaryTimeOff;
            if (isVto && requestSubtype === null) {
                hasError = true;
                errors.requestSubtype = true;
            }
            if (isVto && jobTransferValidationType != null) {
                hasError = true;
                errors.jobTransferValidationType = true;
            }
            if (isOnProbation) {
                if (!probationLength || probationLength < 1) {
                    hasError = true;
                    errors.probationLength = true;
                }
                if (probationDataviewId === null) {
                    hasError = true;
                    errors.probationDataviewId = true;
                }
                if (probationDataviewKeyAlias === '') {
                    hasError = true;
                    errors.probationDataviewKeyAlias = true;
                }
                if (probationCustomDateFormat === null) {
                    hasError = true;
                    errors.probationCustomDateFormat = true;
                }
            }
            if (isOnRestriction) {
                if (restrictionDataviewId === null) {
                    hasError = true;
                    errors.restrictionDataviewId = true;
                }
                if (restrictionDataviewKeyAlias === null || restrictionDataviewKeyAlias === '') {
                    hasError = true;
                    errors.restrictionDataviewKeyAlias = true;
                }
                if (restrictionCustomDateFormat === null) {
                    hasError = true;
                    errors.restrictionCustomDateFormat = true;
                }
                if (restrictionStartAfter === null || restrictionStartAfter < 0 || restrictionStartAfter > 999) {
                    hasError = true;
                    errors.restrictionStartAfter = true;
                }
                if (restrictionDuration === null || restrictionDuration < 1 || restrictionDuration > 999) {
                    hasError = true;
                    errors.restrictionDuration = true;
                }
            }
            if (isEmployeeEligibility) {
                const isShiftRelPeriod = employeeEligibilityPeriod?.startsWith('SHIFT_REL_');
                if (employeeEligibilityLength === null) {
                    hasError = true;
                    errors.employeeEligibilityLength = true;
                }
                if (employeeEligibilityPeriod === null) {
                    hasError = true;
                    errors.employeeEligibilityPeriod = true;
                }
                if (!isShiftRelPeriod) {
                    if (employeeEligibilityDataviewId === null) {
                        hasError = true;
                        errors.employeeEligibilityDataviewId = true;
                    }
                    if (employeeEligibilityDataviewKeyAlias === null) {
                        hasError = true;
                        errors.employeeEligibilityDataviewKeyAlias = true;
                    }
                }
            }
            // Shift rules section
            if (hasAllowOverlapRule(requestType) && isAllowShiftOverlap && overlapLength === null) {
                hasError = true;
                errors.overlapLength = true;
            }
            if (hasValidatePaycodeOverlapRule(requestType) && isValidatePaycodeOverlap && paycodeOverlapLength === null) {
                hasError = true;
                errors.paycodeOverlapLength = true;
            }
            if (hasTimeBeforeOrAfterShiftStartAllowedRule(requestType, requestSubtype) && isTimeBeforeOrAfterShiftStartAllowed && timeBeforeShiftLength === null) {
                if (!timeBeforeShiftLength) {
                    hasError = true;
                    errors.timeBeforeShiftLength = true;
                }
                if (timeBeforeShiftType === null) {
                    hasError = true;
                    errors.timeBeforeShiftType = true;
                }
            }
            if (hasMultipleShiftOnSameDayRule(requestType) && isMultipleShiftOnSameDayRuleEnabled) {
                if (maxMultipleShiftConsecutiveDay === null || maxMultipleShiftConsecutiveDay < 0) {
                    hasError = true;
                    errors.maxMultipleShiftConsecutiveDay = true;
                }
                if (maxMultipleShiftPerWeek === null || maxMultipleShiftPerWeek < 0) {
                    hasError = true;
                    errors.maxMultipleShiftPerWeek = true;
                }
            }
            if (!isVto && isMaxHoursPerRollingPeriod && !maxHoursInRollingPeriodRules.length) {
                hasError = true;
                errors.isMaxHoursPerRollingPeriod = true;
            }
            if (!isVto && isConsecutiveHours) {
                const isConsecutiveDurationValid = (minutes) => minutes >= CONSECUTIVE_MINUTES_MINIMUM && minutes <= CONSECUTIVE_MINUTES_MAXIMUM;
                if (!isMinConsecutiveHours && !isMaxConsecutiveHours) {
                    hasError = true;
                    errors.consecutiveHoursNoBound = true;
                }
                if (isMinConsecutiveHours && !isConsecutiveDurationValid(minConsecutiveHoursInMinutes)) {
                    hasError = true;
                    errors.minConsecutiveHoursInMinutes = true;
                }
                if (isMaxConsecutiveHours && !isConsecutiveDurationValid(maxConsecutiveHoursInMinutes)) {
                    hasError = true;
                    errors.maxConsecutiveHoursInMinutes = true;
                }
                if (isMinConsecutiveHours && isMaxConsecutiveHours && minConsecutiveHoursInMinutes > maxConsecutiveHoursInMinutes) {
                    hasError = true;
                    errors.consecutiveHoursRange = true;
                }
            }
            if (hasAllowOverlapRule(requestType) && isValidatePaycodeOverlap && !overlapPaycodeListToValidate?.included?.length) {
                hasError = true;
                errors.noPaycodesSelected = true;
            }
            if (hasAllowOverlapRule(requestType) && isValidateSameDayPaycode && !sameDayPaycodeListToValidate?.included?.length) {
                hasError = true;
                errors.noSameDayPaycodesSelected = true;
            }
            if((!isMaxHoursPerDay && isOverrideMaxHoursPerDay) 
                || (!isOverrideMaxHoursPerDay && overrideMaxHoursPerDayInMinutes > 0)
                || (isOverrideMaxHoursPerDay && overrideMaxHoursPerDayInMinutes <= 0)) {
                hasError = true;
                errors.overrideMaxHoursPerDayInMinutes = true;
            }
            if((!isMaxHoursPerWeek && isOverrideMaxHoursPerWeek) 
                || (!isOverrideMaxHoursPerWeek && overrideMaxHoursPerWeekInMinutes > 0)
                || (isOverrideMaxHoursPerWeek && overrideMaxHoursPerWeekInMinutes <= 0)) {
                hasError = true;
                errors.overrideMaxHoursPerWeekInMinutes = true;
            }

        }

        if (ruleSetType === RuleSetType.BreakRule) {
            if (breakRules.length <= 0) {
                hasError = true;
                errors.noBreakRuleCondition = true;
            }
            if (breakRules.some(x => x.shiftDuration == 0 || x.actionDuration == 0)) {
                hasError = true;
            }
            const uniqueCombinations = new Set();
            const isBreakRuleAllUnique = breakRules.every(rule => {
                const key = `${rule.condition}_${rule.shiftDuration}`;

                if (uniqueCombinations.has(key)) {
                  return false;
                }
    
                uniqueCombinations.add(key);
                return true;
            });

            if (!isBreakRuleAllUnique) {
                hasError = true;
                errors.noBreakRuleSameConditionAndShiftDuration = true;
            }
        }

        for (const property in errors) {
            if (errors[property] !== validationErrors[property]) {
                setValidationErrors(errors);
            }
        }

        return !hasError;
    };

    const closeSidePanel = () => {
        dispatch({ type: RESET_RULE_SET });
        setValidationErrors(_.clone(DEFAULT_VALIDATION_ERRORS))
        setOpen(false);
    };

    const handleSubmitSetup = () => {
        if (validate()) {
            switch (adminSidePanelAction) {
                case AdminSidePanelAction.Create:
                    dispatch(createRuleSet(adminRuleSet, () => setOpen(false), false));
                    break;
                case AdminSidePanelAction.Duplicate:
                    dispatch(createRuleSet(adminRuleSet, () => setOpen(false), true));
                    break;
                case AdminSidePanelAction.Edit:
                    dispatch(editRuleSet(adminRuleSet, { ruleSets }, () => setOpen(false)));
                    break;
                default:
                    break;
            }
        }
    };

    const formTitle = adminSidePanelAction === AdminSidePanelAction.Edit
        ? t('editRuleSet')
        : t('createRuleSet');
    const isVto = requestType === RequestTypes.voluntaryTimeOff;
    const isFillVto = isVto && requestSubtype === RequestSubtypes.fill;
    let stepNumber = 1;
    return (
        <Drawer
            anchor="right"
            open={open}
            classes={{ paper: 'rse-drawer-paper' }}
        >
            <div className="rse-header-section">
                <div className="title-container">
                    <h3 className="title-label">{formTitle}</h3>
                </div>
                <div>
                    <IconButton
                        className="drawer-x-icon-button"
                        onClick={() => closeSidePanel()}
                        size="large">
                        <FontAwesomeIcon
                            icon={faTimes}
                            size="sm"
                        />
                    </IconButton>
                </div>
            </div>
            <div
                className="rse-content-section"
            >
                <InformationSection validationErrors={validationErrors} isDisabled={!isEditable} stepNumber={stepNumber++}/>
                {ruleSetType === RuleSetType.AwardingRule && (
                    <SortingRuleSection validationErrors={validationErrors} isDisabled={!isEditable} stepNumber={stepNumber++}/>
                )}
                {ruleSetType === RuleSetType.ValidationRule && (
                    <>
                        <EmployeeRulesSection validationErrors={validationErrors} isDisabled={!isEditable}  stepNumber={stepNumber++}/>
                        {!isVto && <ScheduleRulesSection validationErrors={validationErrors} isDisabled={!isEditable}  stepNumber={stepNumber++}/>}
                        {(!isVto || isFillVto) && <ShiftRulesSection validationErrors={validationErrors} isDisabled={!isEditable}  stepNumber={stepNumber++}/>}
                    </>
                )}
                {ruleSetType === RuleSetType.BreakRule && (
                    <BreakRuleSection validationErrors={validationErrors} isDisabled={!isEditable} stepNumber={stepNumber++} />
                )}
            </div>
            <div className="rse-footer-section">
                <ActionButtons
                    cancel={closeSidePanel}
                    submit={handleSubmitSetup}
                    disabledSubmit={isActionLoading}
                    submitText={t('save')}
                    cancelText={t('cancel')}
                />
            </div>
        </Drawer>
    );
}

export default RuleSetEdit;
